import { CommonModule } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateModule } from '@ngx-translate/core';
import { COMPANY_PROFILE_CARD_DATA_TOKEN, CrmInjectionToken } from '@galaxy/crm/static';
import { MatCardModule } from '@angular/material/card';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { ActiveItemsModule } from '@galaxy/inventory-ui';
import { CrmMarketplaceProductsAssociationCardService } from './marketplace-products.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { ConfigurationInterface, WhitelabelService } from '@galaxy/partner';
import { catchError, of, switchMap } from 'rxjs';

const defaultMarket = 'default';

@Component({
  selector: 'crm-marketplace-products-association-card',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatExpansionModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatCardModule,
    GalaxyBadgeModule,
    ActiveItemsModule,
    GalaxyAvatarModule,
    GalaxyLoadingSpinnerModule,
  ],
  templateUrl: './marketplace-products.component.html',
  styleUrls: ['../association-panel-shared.scss', './marketplace-products.component.scss'],
  providers: [CrmMarketplaceProductsAssociationCardService],
})
export class CrmMarketplaceProductsAssociationCardComponent {
  private readonly service = inject(CrmMarketplaceProductsAssociationCardService);
  private readonly data = inject(COMPANY_PROFILE_CARD_DATA_TOKEN);
  private readonly config = inject(CrmInjectionToken);
  private readonly whitelabelService = inject(WhitelabelService);

  private readonly hasAdminPermission$ = this.config.services?.accessService?.hasAdminPermission$ ?? of(false);
  private readonly marketConfig$ = this.config.namespace$.pipe(
    switchMap((partnerId) => this.whitelabelService.getConfiguration(partnerId, this.data.group_id || defaultMarket)),
    catchError(() => of({} as ConfigurationInterface)),
  );

  protected readonly products = toSignal(this.service.products$);
  protected readonly isLoaded = computed(() => this.products() !== undefined);
  protected readonly hasProducts = computed(() => !!this.products()?.length);
  protected readonly hasFailed = toSignal(this.service.getProductsFailed$);
  protected readonly hasAdminPermission = toSignal(this.hasAdminPermission$);
  protected readonly marketConfig = toSignal(this.marketConfig$);
  protected readonly canNavigateToProducts = computed(
    () => this.hasAdminPermission() || this.marketConfig()?.salesConfiguration?.stSalespersonSharedAccountAccess,
  );

  constructor() {
    this.config.namespace$.pipe(takeUntilDestroyed()).subscribe((partnerId) => this.service.setPartnerId(partnerId));
    this.service.setAccountGroupId(this.data.account_group_id ?? '');
    this.service.setMarketId(this.data.group_id || defaultMarket);
  }

  protected handleProductClick(url: string): void {
    const parsedUrl = this.injectAccountId(url, this.data.account_group_id ?? '');
    window.open(parsedUrl, '_blank');
  }

  private injectAccountId(url: string, accountGroupId: string): string {
    return url.replaceAll('<accountId>', accountGroupId).replaceAll('%3CaccountId%3E', accountGroupId);
  }
}
